import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FileText, Edit, Trash2, Plus, Minus } from 'lucide-react';
import axios from 'axios';

import { useAuth, API } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { formatAmount } from '../../utils/format';
const OrdersTab = () => {
  const { getAuthHeaders, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editItems, setEditItems] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeaders()
      });
      // Ordenar órdenes por número de mayor a menor (order_number puede ser
      // numérico o tipo "DEL-123", así que extraemos la parte numérica)
      const list = Array.isArray(response.data) ? [...response.data] : [];
      const orderNum = (o) => {
        const m = String(o?.order_number ?? '').match(/\d+/);
        return m ? parseInt(m[0], 10) : 0;
      };
      list.sort((a, b) => orderNum(b) - orderNum(a));
      setOrders(list);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
    }
  };

  const handleDelete = async (orderId) => {
    if (!confirm('¿Estás seguro de eliminar esta orden?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Orden eliminada');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar orden');
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setEditItems(order.items.map(item => ({
      ...item,
      quantity: item.quantity
    })));
    setShowEditDialog(true);
  };

  const updateItemQuantity = (index, delta) => {
    setEditItems(prev => {
      const newItems = [...prev];
      const newQty = newItems[index].quantity + delta;
      if (newQty > 0) {
        newItems[index] = {
          ...newItems[index],
          quantity: newQty,
          subtotal: newItems[index].unit_price * newQty
        };
      }
      return newItems;
    });
  };

  const removeItem = (index) => {
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveEdit = async () => {
    try {
      const orderData = {
        items: editItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variant_id: item.variant_id,
          notes: item.notes
        })),
        order_type: editingOrder.order_type
      };
      await axios.put(`${API}/orders/${editingOrder.id}`, orderData, {
        headers: getAuthHeaders()
      });
      toast.success('Orden actualizada');
      setShowEditDialog(false);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar orden');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendiente', className: 'status-pending' },
      in_preparation: { label: 'En Preparación', className: 'status-in-preparation' },
      ready: { label: 'Listo', className: 'status-ready' },
      delivered: { label: 'Entregado', className: 'status-delivered' },
      cancelled: { label: 'Cancelado', className: 'status-cancelled' },
    };
    const statusInfo = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const getOrderTypeLabel = (type) => {
    const types = {
      dine_in: 'En Local',
      takeout: 'Para Llevar',
      delivery: 'Delivery'
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Órdenes</h2>
        <div className="text-sm text-gray-600">
          Total: {orders.length} órdenes
        </div>
      </div>

      {/* Safety fallback if orders is not an array */}
      {Array.isArray(orders) && orders.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orders.map((order) => (
          <Card
            key={order.id}
            data-testid={`order-card-${order.id}`}
            className="p-6 bg-white/80 backdrop-blur-xl border-orange-100 hover-lift"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">
                  Orden #{order.order_number}
                </h3>
                {getStatusBadge(order.status)}
              </div>
              <div className="text-right text-sm text-gray-600">
                <p className="font-semibold">{getOrderTypeLabel(order.order_type)}</p>
                <p className="text-xs">
                  {new Date(order.created_at).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {order.customer_name && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">
                  <strong>Cliente:</strong> {order.customer_name}
                </p>
                {order.customer_phone && (
                  <p className="text-sm text-gray-600">
                    <strong>Tel:</strong> {order.customer_phone}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2 mb-4">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-800">
                    {item.quantity}x {item.product_name}
                  </span>
                  <span className="font-semibold">${formatAmount(item.subtotal)}</span>
                </div>
              ))}
            </div>

            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Subtotal:</span>
                <span>${formatAmount(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Impuesto:</span>
                <span>${formatAmount(order.tax)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm mb-1 text-green-600">
                  <span>Descuento:</span>
                  <span>-${formatAmount(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-orange-600">${formatAmount(order.total)}</span>
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(order)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(order.id)}
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay órdenes registradas</p>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Orden #{editingOrder?.order_number}</DialogTitle>
            <DialogDescription>
              Modifica los items y cantidades de la orden
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">{item.product_name}</p>
                  {item.variant_name && (
                    <p className="text-sm text-gray-600">{item.variant_name}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    ${formatAmount(item.unit_price)} c/u
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateItemQuantity(idx, -1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateItemQuantity(idx, 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => removeItem(idx)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {editItems.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                No hay items en la orden
              </p>
            )}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={saveEdit}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                disabled={editItems.length === 0}
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default OrdersTab;
